import { NeedsActionDto } from '@act/shared/data-transfer-objects';
import UpdateIcon from '@mui/icons-material/Update';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { metaSliceSelectors } from 'redux/slices/metaSlice';
import { userAppSelectors } from 'redux/slices/usersSlice';
import { CollapsibleHistoryCard } from './CollapsibleHistoryCard';
import { getHistoryEntryDateLine } from './history-card-helpers';
import './need-history.styles.scss';
import { StyledHistoryEntryDescription } from './history-display';
import { IdentificationBadgeIcon, StrategyIcon } from '@phosphor-icons/react';

interface NeedActionHistoryEntryProps {
  action: NeedsActionDto;
  detailMode?: boolean;
  editMode?: boolean;
  setEdit?: (action: NeedsActionDto) => void;
}

export const NeedActionHistoryEntry = ({
  action,
  detailMode,
  editMode,
  setEdit,
}: NeedActionHistoryEntryProps) => {
  const userMap = userAppSelectors.useSelectUsersMap();
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const activities = needMetadata?.data?.activities;
  const [latestAction, restOfActions] = useMemo(() => {
    const allActions = [action, ...(action.subsequentActions || [])];
    allActions.sort((a, b) => b.createdAt - a.createdAt);
    return [allActions[0], detailMode ? allActions.slice(1) : []];
  }, [action, detailMode]);

  function getActionTitle(action: NeedsActionDto): string {
    const relevantAction = action?.subsequentActions?.length
      ? [...action.subsequentActions].sort(
          (a, b) => b.createdAt - a.createdAt
        )[0]
      : action;

    return activities?.find(i => i.id === relevantAction?.activityId)?.label;
  }

  const actionTitle = getActionTitle(action);
  const hasSubsequentActions = !!action.subsequentActions?.length;

  const uniqueResourceCount = useMemo(() => {
    // Collect all resourceReferrals from main action and subsequentActions
    const allResourceReferrals = [
      ...(action.resourceReferrals || []),
      ...(action.subsequentActions || []).flatMap(
        sa => sa.resourceReferrals || []
      ),
    ];
    // Get unique resources by id
    const uniqueResourceIds = new Set(
      allResourceReferrals.map(ref => ref.id).filter(id => id !== undefined)
    );

    return uniqueResourceIds.size;
  }, [action]);

  return (
    <>
      <CollapsibleHistoryCard
        title={actionTitle}
        subtitle={getHistoryEntryDateLine(
          DateTime.fromISO(latestAction.date).toMillis(),
          userMap && userMap[latestAction.creatorId]?.name
        )}
        icon={
          hasSubsequentActions ? (
            <UpdateIcon color="primary" />
          ) : (
            <StrategyIcon />
          )
        }
        expandedContent={
          <Box display="flex" flexDirection="column" gap="16px">
            <StyledHistoryEntryDescription
              dateAndUser={getHistoryEntryDateLine(
                latestAction.createdAt,
                userMap && userMap[latestAction.creatorId]?.name
              )}
              description={latestAction.description}
            ></StyledHistoryEntryDescription>
            {uniqueResourceCount > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <IdentificationBadgeIcon size={16} />
                <Typography variant="bodySmall" className="resource-count">
                  {uniqueResourceCount} resource
                  {uniqueResourceCount !== 1 ? 's' : ''} referred
                </Typography>
              </div>
            )}
            {!!restOfActions.length && (
              <>
                {restOfActions.map(a => (
                  <div key={a.id}>
                    <StyledHistoryEntryDescription
                      dateAndUser={getHistoryEntryDateLine(
                        a.createdAt,
                        userMap && userMap[a.creatorId]?.name
                      )}
                      description={a.description}
                    ></StyledHistoryEntryDescription>
                  </div>
                ))}
              </>
            )}
          </Box>
        }
        showEditButton={editMode}
        onEdit={() => {
          setEdit?.(action);
        }}
      />
    </>
  );
};
